import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  listReports,
  getReport,
  updateReport,
  suspendAccount,
  deleteAccount,
  moderatePost,
  getPostDetail,
} from "../../Service/moderationApi";
import "./moderation.css";

const profileUrl = (hccId) => `https://indiacastingclub.com/profile/${hccId}`;
const postUrl = (uid) => `https://indiacastingclub.com/p/${uid}`;

const FRAUD_STATUSES = ["pending", "investigating", "resolved", "rejected"];
const REPORT_TYPES = ["post", "account", "message"];

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const reporterName = (reporter) => {
  if (!reporter) return "--";
  const name = [reporter.first_name, reporter.last_name].filter(Boolean).join(" ");
  return name || reporter.hcc_id || "--";
};

const ReportCard = ({ row, onOpen }) => {
  const status = row?.fraud_status || "pending";
  return (
    <article
      className={`moderation-card moderation-card--${status}`}
      onClick={() => onOpen(row)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onOpen(row)}
    >
      <div className="moderation-card-header">
        <div className="moderation-card-header-left">
          <span className="moderation-id">#{row?.id}</span>
          <span className={`moderation-status-badge ${status}`}>{status}</span>
          {row?.report_type ? (
            <span className="moderation-type-badge">{row.report_type}</span>
          ) : null}
        </div>
        <span style={{ fontSize: 12, color: "#666" }}>{formatDateTime(row?.cdate)}</span>
      </div>
      <div className="moderation-card-body">
        <div className="moderation-description">{row?.description || "No description"}</div>
        <div className="moderation-meta-strip">
          <div className="moderation-meta-item">
            <label>Reporter</label>
            <span>
              {reporterName(row?.reporter)}
              {row?.reporter?.hcc_id ? ` (${row.reporter.hcc_id})` : ""}
            </span>
          </div>
          {row?.reported_profile_hcc_id || row?.reported_profile ? (
            <div className="moderation-meta-item">
              <label>Reported profile</label>
              <span>{row?.reported_profile_hcc_id || `#${row.reported_profile}`}</span>
            </div>
          ) : null}
          {row?.reported_post ? (
            <div className="moderation-meta-item">
              <label>Reported post</label>
              <span>#{row.reported_post}</span>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
};

const ReportsInbox = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowPerPage, setRowPerPage] = useState(20);
  const [fraudStatus, setFraudStatus] = useState("pending");
  const [reportType, setReportType] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [statusDraft, setStatusDraft] = useState("pending");
  const [actionReason, setActionReason] = useState("");
  const [actionError, setActionError] = useState("");
  const [targetProfileId, setTargetProfileId] = useState(null);

  const getData = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, page_size: rowPerPage };
      if (fraudStatus) params.fraud_status = fraudStatus;
      if (reportType) params.report_type = reportType;
      if (search) params.search = search;

      const response = await listReports(params);
      setRows(response?.data?.results || []);
      setTotalRows(response?.data?.count || 0);
    } catch (error) {
      console.error(error);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [currentPage, rowPerPage, fraudStatus, reportType, search]);

  const resolveTargetProfile = async (detail) => {
    if (detail?.reported_profile) return detail.reported_profile;
    if (!detail?.reported_post) return null;
    try {
      const res = await getPostDetail(detail.reported_post);
      return res.data?.profile?.id ?? res.data?.profile ?? null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const openReport = async (row) => {
    setShowModal(true);
    setSelected(row);
    setAdminNotes(row?.admin_notes || "");
    setStatusDraft(row?.fraud_status || "pending");
    setActionReason("");
    setActionError("");
    setTargetProfileId(row?.reported_profile || null);

    setDetailLoading(true);
    try {
      const res = await getReport(row.id);
      const detail = res.data;
      setSelected(detail);
      setAdminNotes(detail?.admin_notes || "");
      setStatusDraft(detail?.fraud_status || "pending");
      const profileId = await resolveTargetProfile(detail);
      setTargetProfileId(profileId);
    } catch (error) {
      console.error(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeModal = () => {
    if (actionLoading) return;
    setShowModal(false);
    setSelected(null);
    setActionError("");
  };

  const handleSaveReport = async () => {
    if (!selected?.id) return;
    setActionLoading(true);
    setActionError("");
    try {
      await updateReport(selected.id, {
        fraud_status: statusDraft,
        admin_notes: adminNotes,
      });
      closeModal();
      getData();
    } catch (error) {
      setActionError(error?.response?.data?.detail || "Failed to update report.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspendAccount = async () => {
    const profileId = targetProfileId;
    if (!profileId) {
      setActionError("No target profile found for this report.");
      return;
    }
    if (!actionReason.trim()) {
      setActionError("Reason is required to suspend an account.");
      return;
    }
    setActionLoading(true);
    setActionError("");
    try {
      await suspendAccount(profileId, actionReason.trim());
      await updateReport(selected.id, {
        fraud_status: "resolved",
        admin_notes: adminNotes || `Account suspended: ${actionReason.trim()}`,
      });
      closeModal();
      getData();
    } catch (error) {
      setActionError(error?.response?.data?.detail || "Failed to suspend account.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const profileId = targetProfileId;
    if (!profileId) {
      setActionError("No target profile found for this report.");
      return;
    }
    if (!actionReason.trim()) {
      setActionError("Reason is required to delete an account.");
      return;
    }
    if (!window.confirm("Permanently soft-delete this account? This cannot be undone from the dashboard.")) {
      return;
    }
    setActionLoading(true);
    setActionError("");
    try {
      await deleteAccount(profileId, actionReason.trim());
      await updateReport(selected.id, {
        fraud_status: "resolved",
        admin_notes: adminNotes || `Account deleted: ${actionReason.trim()}`,
      });
      closeModal();
      getData();
    } catch (error) {
      setActionError(error?.response?.data?.detail || "Failed to delete account.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectPost = async () => {
    const postId = selected?.reported_post;
    if (!postId) {
      setActionError("No reported post on this report.");
      return;
    }
    setActionLoading(true);
    setActionError("");
    try {
      await moderatePost(postId, "reject", actionReason.trim() || "Rejected via report triage");
      await updateReport(selected.id, {
        fraud_status: "resolved",
        admin_notes: adminNotes || "Post rejected during report triage.",
      });
      closeModal();
      getData();
    } catch (error) {
      setActionError(error?.response?.data?.detail || "Failed to reject post.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalRows / rowPerPage));
  const rangeStart = totalRows === 0 ? 0 : (currentPage - 1) * rowPerPage + 1;
  const rangeEnd = Math.min(currentPage * rowPerPage, totalRows);

  return (
    <div className="child-sidenav">
      <div className="childnav-content user-screen moderation-page">
        <div className="moderation-toolbar">
          <div className="btn-group" role="group" aria-label="fraud status">
            <button
              type="button"
              className={`btn btn-sm ${fraudStatus === "pending" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => { setFraudStatus("pending"); setCurrentPage(1); }}
            >
              Pending
            </button>
            <button
              type="button"
              className={`btn btn-sm ${fraudStatus === "investigating" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => { setFraudStatus("investigating"); setCurrentPage(1); }}
            >
              Investigating
            </button>
            <button
              type="button"
              className={`btn btn-sm ${fraudStatus === "resolved" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => { setFraudStatus("resolved"); setCurrentPage(1); }}
            >
              Resolved
            </button>
            <button
              type="button"
              className={`btn btn-sm ${fraudStatus === "rejected" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => { setFraudStatus("rejected"); setCurrentPage(1); }}
            >
              Rejected
            </button>
            <button
              type="button"
              className={`btn btn-sm ${fraudStatus === "" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => { setFraudStatus(""); setCurrentPage(1); }}
            >
              All
            </button>
          </div>

          <select
            className="form-select form-select-sm"
            style={{ width: "auto" }}
            value={reportType}
            onChange={(e) => { setReportType(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All types</option>
            {REPORT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          <form className="moderation-search" onSubmit={handleSearch}>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search HCC ID or description"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-sm btn-outline-secondary">Search</button>
          </form>

          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={getData}>
            Refresh
          </button>

          <div className="moderation-toolbar-summary">
            <b>{totalRows}</b> reports
          </div>
        </div>

        {loading ? (
          <div className="moderation-loader">
            <img src={require("../../Components/FormParser/images/loader2.gif")} alt="Loading..." />
          </div>
        ) : rows.length === 0 ? (
          <div className="moderation-empty">No reports found.</div>
        ) : (
          <div className="moderation-list">
            {rows.map((row) => (
              <ReportCard key={row.id} row={row} onOpen={openReport} />
            ))}
          </div>
        )}

        {totalRows > 0 && (
          <div className="moderation-pagination">
            <div>
              Rows per page:{" "}
              <select
                value={rowPerPage}
                onChange={(e) => { setRowPerPage(Number(e.target.value)); setCurrentPage(1); }}
              >
                {[10, 20, 25, 30, 50].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div>{rangeStart}-{rangeEnd} of {totalRows}</div>
            <div className="moderation-pagination-controls">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Previous
              </button>
              <span>Page {currentPage} / {totalPages}</span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal centered size="lg" show={showModal} onHide={closeModal}>
        <Modal.Header closeButton={!actionLoading}>
          <Modal.Title>Report #{selected?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className="text-center py-4">Loading report details...</div>
          ) : selected ? (
            <>
              <div className="moderation-detail-section">
                <h6>Summary</h6>
                <div className="moderation-meta-strip">
                  <div className="moderation-meta-item">
                    <label>Type</label>
                    <span>{selected.report_type || selected.category || "--"}</span>
                  </div>
                  <div className="moderation-meta-item">
                    <label>Submitted</label>
                    <span>{formatDateTime(selected.cdate)}</span>
                  </div>
                  <div className="moderation-meta-item">
                    <label>Reporter</label>
                    <span>
                      {reporterName(selected.reporter)}
                      {selected.reporter?.hcc_id ? ` (${selected.reporter.hcc_id})` : ""}
                    </span>
                  </div>
                </div>
                <p className="mt-2 mb-0">{selected.description || "No description"}</p>
              </div>

              {(selected.reported_profile_hcc_id || selected.reported_profile) && (
                <div className="moderation-detail-section">
                  <h6>Reported profile</h6>
                  <span>
                    {selected.reported_profile_hcc_id || `#${selected.reported_profile}`}
                    {selected.reported_profile_hcc_id ? (
                      <>
                        {" · "}
                        <a href={profileUrl(selected.reported_profile_hcc_id)} target="_blank" rel="noopener noreferrer">
                          View profile
                        </a>
                      </>
                    ) : null}
                  </span>
                </div>
              )}

              {selected.reported_post && (
                <div className="moderation-detail-section">
                  <h6>Reported post</h6>
                  <span>
                    #{selected.reported_post}
                    {selected.reported_post_uid ? (
                      <>
                        {" · "}
                        <a href={postUrl(selected.reported_post_uid)} target="_blank" rel="noopener noreferrer">
                          View post
                        </a>
                      </>
                    ) : null}
                  </span>
                </div>
              )}

              <div className="moderation-detail-section">
                <label className="form-label"><b>Status</b></label>
                <select
                  className="form-select"
                  value={statusDraft}
                  disabled={actionLoading}
                  onChange={(e) => setStatusDraft(e.target.value)}
                >
                  {FRAUD_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="moderation-detail-section">
                <label className="form-label"><b>Admin notes</b></label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={adminNotes}
                  disabled={actionLoading}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Internal notes for this report..."
                />
              </div>

              <div className="moderation-detail-section">
                <label className="form-label"><b>Action reason</b> (required for account actions)</label>
                <input
                  type="text"
                  className="form-control"
                  value={actionReason}
                  disabled={actionLoading}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder="Policy violation details..."
                />
              </div>

              {actionError ? (
                <div className="alert alert-danger py-2">{actionError}</div>
              ) : null}

              <div className="moderation-action-row">
                {targetProfileId ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-warning btn-sm"
                      disabled={actionLoading}
                      onClick={handleSuspendAccount}
                    >
                      Suspend account
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      disabled={actionLoading}
                      onClick={handleDeleteAccount}
                    >
                      Delete account
                    </button>
                  </>
                ) : null}
                {selected.reported_post ? (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    disabled={actionLoading}
                    onClick={handleRejectPost}
                  >
                    Reject post
                  </button>
                ) : null}
              </div>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" disabled={actionLoading} onClick={closeModal}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!selected || actionLoading}
            onClick={handleSaveReport}
          >
            {actionLoading ? "Saving..." : "Save status & notes"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export { ReportsInbox };
